import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  GitBranch, 
  X, 
  ArrowRight, 
  Plus, 
  Minus, 
  RotateCw, 
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const PlanComparison = ({ planId, version1, version2, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [diffData, setDiffData] = useState(null);

  useEffect(() => {
    if (planId && version1 && version2) {
      fetchVersionDiff();
    }
  }, [planId, version1, version2]);

  const fetchVersionDiff = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/monetization/plans/${planId}/versions/${version1}/diff/${version2}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setDiffData(data);
      } else {
        setError('Failed to load version comparison');
      }
    } catch (err) {
      console.error('Error fetching version diff:', err);
      setError('Failed to load version comparison');
    } finally {
      setLoading(false);
    }
  };

  const getChangeIcon = (changeType) => {
    switch (changeType) {
      case 'added': return <Plus className="w-4 h-4 text-green-600" />;
      case 'removed': return <Minus className="w-4 h-4 text-red-600" />;
      case 'modified': return <RotateCw className="w-4 h-4 text-blue-600" />;
      default: return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'added': return 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800';
      case 'removed': return 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800';
      case 'modified': return 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800';
      default: return 'bg-gray-50 border-gray-200 dark:bg-gray-900/10 dark:border-gray-600';
    }
  };

  const formatJsonValue = (value) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }
    
    if (typeof value === 'object') {
      return (
        <pre className="text-xs bg-gray-100 p-2 rounded dark:bg-gray-800 overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    
    return <span className="font-mono text-sm">{String(value)}</span>;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-screen overflow-y-auto dark:bg-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-300">Comparing versions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-7xl w-full max-h-screen overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <GitBranch className="w-6 h-6 text-indigo-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Plan Comparison</h2>
              <p className="text-gray-600 dark:text-gray-300">
                Version {version1} <ArrowRight className="inline w-4 h-4 mx-2" /> Version {version2}
              </p>
            </div>
          </div>
          <Button onClick={onClose} variant="outline" size="sm">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        {diffData && (
          <>
            {/* Summary */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {diffData.change_count}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Changes</div>
              </div>
              
              <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
                <div className="text-2xl font-bold text-green-600">
                  {diffData.changes?.filter(c => c.type === 'added').length || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Added</div>
              </div>
              
              <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
                <div className="text-2xl font-bold text-blue-600">
                  {diffData.changes?.filter(c => c.type === 'modified').length || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Modified</div>
              </div>
            </div>

            {/* Changes List */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Changes</h3>
              
              {diffData.changes && diffData.changes.length > 0 ? (
                <div className="space-y-3">
                  {diffData.changes.map((change, index) => (
                    <div 
                      key={index} 
                      className={`border rounded-lg p-4 ${getChangeColor(change.type)}`}
                    >
                      <div className="flex items-start gap-3">
                        {getChangeIcon(change.type)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {change.path}
                            </span>
                            <span className="text-xs px-2 py-1 rounded-full bg-white/50 dark:bg-gray-900/50 capitalize">
                              {change.type}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Old Value */}
                            {change.old_value !== null && (
                              <div>
                                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  Version {version1} (Before)
                                </div>
                                <div className="bg-white rounded border p-3 dark:bg-gray-900 dark:border-gray-600">
                                  {formatJsonValue(change.old_value)}
                                </div>
                              </div>
                            )}
                            
                            {/* New Value */}
                            {change.new_value !== null && (
                              <div>
                                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  Version {version2} (After)
                                </div>
                                <div className="bg-white rounded border p-3 dark:bg-gray-900 dark:border-gray-600">
                                  {formatJsonValue(change.new_value)}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-1 dark:text-gray-100">No changes found</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Versions {version1} and {version2} are identical
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlanComparison;